import { TherapyStatusBadge } from '@app/icare/components/status/TherapyStatusBadge'
import { PatientOrderFormTypeEnum } from '@app/icare/db/schema/form/PatientFormType'
import { patientQueryOptions } from '@app/icare/entities/patient/queries/patientQueryOptions'
import { patientTherapyQueries } from '@app/icare/entities/patient/queries/patientTherapyQueries'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import { Therapies, TherapyByType } from '@app/icare/entities/therapy/schema/Therapy'
import { TherapyTypeEnum } from '@app/icare/entities/therapy/schema/TherapyType'
import { mapPatientTherapyToTherapyStatus } from '@app/icare/shared/api'
import { Trans } from '@lingui/react/macro'
import { entityParams } from '@pkg/db/utils'
import { AddButton } from '@pkg/ui/button'
import { useRouterTabs } from '@pkg/ui/hooks/useRouterTabs'
import { useExtra, useTabs } from '@pkg/ui/layout/PageContainer'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Outlet, useRouter } from '@tanstack/react-router'
import { Dropdown, Space } from 'antd'

export const Route = createFileRoute('/_app/patienten/$id/_edit/_documents/bedarfsermittlungen')({
  loader: ({ params, context: { queryClient } }) =>
    queryClient.ensureQueryData(patientTherapyQueries.forIndex(params.id)),
  component: RouteComponent,
  params: entityParams({ id: PatientId }),
})

function RouteComponent() {
  const { id } = Route.useParams()
  const { data: patient } = useSuspenseQuery(patientQueryOptions(id))
  const { data: patientTherapies = [] } = useSuspenseQuery(patientTherapyQueries.forIndex(id))
  const router = useRouter()

  const therapies = patientTherapies
    .filter(therapy => {
      const therapyType = Therapies.find(t => t.type === therapy.type)
      return (therapyType?.orders.length ?? 0) > 0
    })
    .map(t => {
      const status = mapPatientTherapyToTherapyStatus(t, patient?.deceased)
      return {
        ...t,
        status,
        label: (
          <Space>
            {TherapyTypeEnum.$.getLabel(t.type)}
            <TherapyStatusBadge value={status} />
          </Space>
        ),
      }
    })

  const tabs = useRouterTabs(
    lastMatch => `/patienten/$id/bedarfsermittlungen/${(lastMatch.params as { therapy?: string }).therapy ?? ''}`,
  )

  useTabs(
    {
      ...tabs,
      items: [
        {
          key: '/patienten/$id/bedarfsermittlungen',
          label: <Trans>Übersicht</Trans>,
        },
        ...therapies.map(therapy => ({
          key: `/patienten/$id/bedarfsermittlungen/${therapy.id}`,
          label: therapy.label,
        })),
      ],
    },
    [tabs.activeKey],
  )

  useExtra(
    therapies.length > 0 ? (
      <Dropdown
        menu={{
          items: therapies.map(therapy => {
            const orders = TherapyByType[therapy.type].orders
            const therapyIsEnded = therapy.status === 'ENDED'
            const clickHandler = (order: typeof PatientOrderFormTypeEnum.$.Type) =>
              void router.navigate({
                to: '/patienten/$id/bedarfsermittlung/new',
                params: { id },
                search: {
                  therapyId: therapy.id,
                  order,
                },
              })

            return {
              key: therapy.id,
              disabled: therapyIsEnded,
              label: therapy.label,

              ...(orders.length === 1
                ? {
                    onClick: () => clickHandler(orders[0] as typeof PatientOrderFormTypeEnum.$.Type),
                  }
                : {
                    type: 'group' as const,
                    children: orders.map(order => ({
                      key: `${therapy.id}-${order}`,
                      disabled: therapyIsEnded,
                      label: PatientOrderFormTypeEnum.$.getLabel(order),
                      onClick: () => clickHandler(order),
                    })),
                  }),
            }
          }),
        }}
      >
        <AddButton>
          <Trans>Bedarfsermittlung erstellen</Trans>
        </AddButton>
      </Dropdown>
    ) : null,
    [therapies.length],
  )

  return <Outlet />
}
